'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';

import { HEADER_BANNER_READY_EVENT } from '@/constants';

const DIR_THRESHOLD = 4;
const DIR_ACCUMULATE = 16;
const TOP_THRESHOLD = 50;

export const useMobileHeaderScroll = (enabled, { isShop = false } = {}) => {
	const headerRef = useRef(null);
	const collapseLayerRef = useRef(null);
	const navSlotRef = useRef(null);
	const navRef = useRef(null);
	const fulfillmentRef = useRef(null);
	const heightsRef = useRef({ banner: 0, nav: 0, fulfillment: 0 });
	const lastScrollYRef = useRef(0);
	const isNavHiddenRef = useRef(false);
	const resizeTimerRef = useRef(0);
	const scrollDeltaRef = useRef(0);

	const readCssLength = (varName) => {
		const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
		const value = parseFloat(raw);
		return Number.isNaN(value) ? 0 : Math.ceil(value);
	};

	const readBannerHeight = () => {
		const headerEl = headerRef.current;
		const wrapperEl =
			headerEl?.querySelector('.header-banner-slot .wrapper-contact') ||
			headerEl?.querySelector('.wrapper-contact');
		const minBanner = readCssLength('--headerBannerHeight');

		let measured = 0;
		if (wrapperEl) {
			measured = Math.ceil(wrapperEl.getBoundingClientRect().height);
		}

		const banner = Math.max(measured, minBanner);

		if (banner > 0) {
			heightsRef.current.banner = banner;
		}
	};

	const readNavHeight = () => {
		const navEl = navRef.current;
		if (!navEl) return;

		const navH = Math.ceil(navEl.scrollHeight);
		if (navH > 0) {
			heightsRef.current.nav = navH;
			return;
		}

		const cssNav = parseInt(
			getComputedStyle(document.documentElement).getPropertyValue('--headerFixedHeight').trim(),
			10
		);

		if (!Number.isNaN(cssNav) && cssNav > 0) {
			heightsRef.current.nav = cssNav;
		}
	};

	const readFulfillmentHeight = () => {
		heightsRef.current.fulfillment = 0;
		if (!isShop) return;

		const fulfillmentEl = fulfillmentRef.current;
		if (!fulfillmentEl) return;

		const fulfillmentH = fulfillmentEl.getBoundingClientRect().height;
		if (fulfillmentH > 0) {
			heightsRef.current.fulfillment = fulfillmentH;
		}
	};

	const applyLayoutVars = () => {
		const { banner, nav, fulfillment } = heightsRef.current;
		const total = Math.ceil(banner + nav + (isShop ? fulfillment : 0));
		const collapsed = nav > 0 ? total - nav : Math.ceil(banner + (isShop ? fulfillment : 0));

		if (banner > 0) {
			document.documentElement.style.setProperty('--header-banner-slot-height', `${banner}px`);
		}

		if (nav > 0) {
			document.documentElement.style.setProperty('--nav-height', `${nav}px`);
		}

		if (total > 0) {
			document.documentElement.style.setProperty('--header-total-height', `${total}px`);
		}

		if (collapsed > 0) {
			document.documentElement.style.setProperty('--header-collapsed-bottom', `${collapsed}px`);
		}
	};

	const lockHeights = () => {
		readBannerHeight();
		readNavHeight();
		readFulfillmentHeight();
		applyLayoutVars();
	};

	const setNavHidden = (hidden) => {
		if (isNavHiddenRef.current === hidden) return;

		isNavHiddenRef.current = hidden;
		headerRef.current?.classList.toggle('is-nav-hidden', hidden);
		headerRef.current?.classList.add('is-header-animating');
		document.body.classList.add('is-header-animating');
		document.body.classList.toggle('is-header-nav-hidden', hidden);
		window.dispatchEvent(new CustomEvent('mobile-header-nav-toggle'));
	};

	const syncNavForScrollPosition = () => {
		const scrollY = window.scrollY ?? document.documentElement.scrollTop;
		const shouldHide = scrollY > TOP_THRESHOLD;

		lastScrollYRef.current = scrollY;
		scrollDeltaRef.current = 0;

		if (isNavHiddenRef.current === shouldHide) return;

		isNavHiddenRef.current = shouldHide;
		headerRef.current?.classList.toggle('is-nav-hidden', shouldHide);
		document.body.classList.toggle('is-header-nav-hidden', shouldHide);
		window.dispatchEvent(new CustomEvent('mobile-header-nav-toggle'));
	};

	const onScroll = () => {
		if (document.body.classList.contains('is-catalog-initial-scroll')) return;

		const scrollY = window.scrollY ?? document.documentElement.scrollTop;
		const delta = scrollY - lastScrollYRef.current;
		lastScrollYRef.current = scrollY;

		if (scrollY <= TOP_THRESHOLD) {
			scrollDeltaRef.current = 0;
			setNavHidden(false);
			return;
		}

		if (Math.abs(delta) >= DIR_THRESHOLD) {
			scrollDeltaRef.current += delta;
		}

		if (scrollDeltaRef.current > DIR_ACCUMULATE) {
			scrollDeltaRef.current = 0;
			setNavHidden(true);
		} else if (scrollDeltaRef.current < -DIR_ACCUMULATE) {
			scrollDeltaRef.current = 0;
			setNavHidden(false);
		}
	};

	useLayoutEffect(() => {
		if (!enabled) return undefined;

		const minBanner = readCssLength('--headerBannerHeight');
		const minNav = readCssLength('--headerFixedHeight');

		heightsRef.current.banner = minBanner;
		heightsRef.current.nav = minNav;
		applyLayoutVars();

		lockHeights();
		const rafId = requestAnimationFrame(lockHeights);

		return () => cancelAnimationFrame(rafId);
	}, [enabled, isShop]);

	useEffect(() => {
		if (!enabled) {
			isNavHiddenRef.current = false;
			headerRef.current?.classList.remove('is-nav-hidden');
			headerRef.current?.classList.remove('is-header-animating');
			document.body.classList.remove('is-header-animating');
			document.body.classList.remove('is-header-nav-hidden');
			document.documentElement.style.removeProperty('--header-total-height');
			document.documentElement.style.removeProperty('--header-collapsed-bottom');
			document.documentElement.style.removeProperty('--nav-height');
			document.documentElement.style.removeProperty('--header-banner-slot-height');
			return undefined;
		}

		lastScrollYRef.current = window.scrollY ?? document.documentElement.scrollTop;
		onScroll();

		const layer = collapseLayerRef.current;
		let animationTimer = 0;

		const clearHeaderAnimating = () => {
			clearTimeout(animationTimer);
			headerRef.current?.classList.remove('is-header-animating');
			document.body.classList.remove('is-header-animating');
			window.dispatchEvent(new CustomEvent('mobile-header-offset-change'));
		};

		const onTransitionEnd = (event) => {
			if (event.target !== layer || event.propertyName !== 'transform') return;
			clearHeaderAnimating();
		};

		const scheduleHeaderAnimatingClear = () => {
			clearTimeout(animationTimer);
			animationTimer = setTimeout(clearHeaderAnimating, 400);
		};

		const onNavToggle = () => {
			scheduleHeaderAnimatingClear();
		};

		layer?.addEventListener('transitionend', onTransitionEnd);
		window.addEventListener('mobile-header-nav-toggle', onNavToggle);

		const onResize = () => {
			clearTimeout(resizeTimerRef.current);
			resizeTimerRef.current = setTimeout(lockHeights, 150);
		};

		window.addEventListener('resize', onResize, { passive: true });
		window.addEventListener(HEADER_BANNER_READY_EVENT, lockHeights);
		window.addEventListener('catalog-initial-scroll-end', syncNavForScrollPosition);

		let scrollRaf = 0;
		const handleScroll = () => {
			if (scrollRaf) return;
			scrollRaf = requestAnimationFrame(() => {
				scrollRaf = 0;
				onScroll();
			});
		};

		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			layer?.removeEventListener('transitionend', onTransitionEnd);
			window.removeEventListener('mobile-header-nav-toggle', onNavToggle);
			clearTimeout(animationTimer);
			clearTimeout(resizeTimerRef.current);
			if (scrollRaf) cancelAnimationFrame(scrollRaf);
			window.removeEventListener('resize', onResize);
			window.removeEventListener(HEADER_BANNER_READY_EVENT, lockHeights);
			window.removeEventListener('catalog-initial-scroll-end', syncNavForScrollPosition);
			window.removeEventListener('scroll', handleScroll);
			headerRef.current?.classList.remove('is-nav-hidden');
			headerRef.current?.classList.remove('is-header-animating');
			document.body.classList.remove('is-header-animating');
			document.body.classList.remove('is-header-nav-hidden');
			document.documentElement.style.removeProperty('--header-total-height');
			document.documentElement.style.removeProperty('--header-collapsed-bottom');
			document.documentElement.style.removeProperty('--nav-height');
			document.documentElement.style.removeProperty('--header-banner-slot-height');
		};
	}, [enabled, isShop]);

	return { headerRef, navSlotRef, navRef, fulfillmentRef, collapseLayerRef };
};
